import { useConfig } from '@lore/config';
import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import usePublicConversation from '@/hooks/usePublicConversation';
import PrimaryButton from './PrimaryButton';
import FormField from './FormField';
import SuccessPanel from './SuccessPanel';
import { Turnstile } from '@marsidev/react-turnstile'

InterestForm.propTypes = {
  onSubmit: PropTypes.func,
  itemType: PropTypes.oneOf(['project', 'event']),
  itemName: PropTypes.string,
  itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Preview-only props for the design system; do not pass in production.
  forceLoading: PropTypes.bool,
  forceSuccess: PropTypes.bool
};

export default function InterestForm(props) {
  const {
    onSubmit,
    itemType = 'project',
    itemName,
    itemId,
    forceLoading = false,
    forceSuccess = false
  } = props;
  const config = useConfig();
  const { turnstileSiteKey } = config.custom;
  const { t } = useTranslation();
  const { submit, isLoading: isSubmitting, isSuccess } = usePublicConversation();
  const turnstileRef = useRef(null)
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileError, setTurnstileError] = useState('')

  const [form, setForm] = useState({
    firstName: '',
    surname: '',
    email: '',
    phone: '',
    message: t('projects.microsite.interest_form.default_message') // Default message
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const isLoading = forceLoading || isSubmitting;
  const isSubmitted = forceSuccess || submitted || isSuccess;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = t('projects.microsite.interest_form.error_first_name_required');
    if (!form.surname.trim()) e.surname = t('projects.microsite.interest_form.error_surname_required');
    if (!form.email.trim()) e.email = t('projects.microsite.interest_form.error_email_required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = t('projects.microsite.interest_form.error_email_invalid');
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    const itemLink = itemId ? `${window.location.origin}/${itemType}/${itemId}` : '';
    const itemMessage = !itemName
      ? t('projects.microsite.interest_form.default_message')
      : itemType === 'event'
        ? t('projects.microsite.interest_form.event_interest_message', { eventName: itemName })
        : t('projects.microsite.interest_form.project_interest_message', { projectName: itemName });
    const basicMessage = itemLink ? `${itemMessage} ${itemLink}` : itemMessage;
    const finalMessage = `${basicMessage}\n\n${form.firstName} ${form.surname}\n${form.email}\n${form.phone}`;

    const payload = {
      firstName: form.firstName,
      lastName: form.surname,
      email: form.email,
      phoneNumber: form.phone,
      clientToken: turnstileToken,
      message: finalMessage
    };

    if (onSubmit) {
      onSubmit(payload);
    } else {
      submit(payload, {
        onSuccess: () => {
          setSubmitted(true);
          turnstileRef.current?.reset();
          setTurnstileToken('');
        },
        onError: () => {
          setErrors({ submit: t('projects.microsite.interest_form.error_generic') });
        }
      });
    }
  };

  return (
    <article className="w-full max-w-screen-xl mx-auto bg-white rounded-cs-10 shadow-lg overflow-hidden">
      {/* Header */}
      <header className="bg-cs-orange-650 px-4 py-5">
        <p className="text-white text-base leading-normal font-medium">
          {t('projects.microsite.interest_form.header_tag')}
        </p>
        <h2 className="mt-1 text-white text-3xl leading-tight font-extrabold">
          {t('projects.microsite.interest_form.header_title')}
        </h2>
      </header>

      {/* Body */}
      {isSubmitted ? (
        <SuccessPanel />
      ) : (
        <form onSubmit={handleSubmit} noValidate>
          <div className="px-4 py-6 flex flex-col gap-8 md:flex-row md:gap-10">

            {/* Left — what to expect */}
            <div className="flex flex-col gap-3 w-full md:flex-1">
              <h3 className="text-base font-semibold text-cs-gray-900">{t('projects.microsite.interest_form.expect_title')}</h3>
              <p className="text-sm leading-relaxed text-cs-gray-500">
                {t('projects.microsite.interest_form.expect_p1')}
              </p>
              <p className="text-sm leading-relaxed text-cs-gray-500">
                {t('projects.microsite.interest_form.expect_p2')}
              </p>
              <p className="text-sm leading-relaxed text-cs-gray-500">
                {t('projects.microsite.interest_form.expect_p3')}{' '}
                <a
                  href="mailto:connect@communityshare.org"
                  className="text-cs-navy-800 font-semibold underline hover:opacity-75 focus:outline-none focus-visible:ring-2 focus-visible:ring-black rounded-sm"
                >
                  {t('projects.microsite.interest_form.email_us_link')}
                </a>
                .
              </p>
            </div>

            {/* Divider — horizontal on mobile, vertical on md+ */}
            <div className="h-px w-full bg-cs-gray-300 md:hidden" />
            <div className="hidden md:block w-px bg-cs-gray-300 self-stretch" />

            {/* Right — form fields */}
            <div className="flex flex-col gap-5 w-full md:flex-1">
              <FormField
                label={t('projects.microsite.interest_form.first_name_label')}
                name="firstName"
                required
                value={form.firstName}
                onChange={handleChange}
                error={errors.firstName}
                placeholder={t('projects.microsite.interest_form.first_name_placeholder')}
              />
              <FormField
                label={t('projects.microsite.interest_form.surname_label')}
                name="surname"
                required
                value={form.surname}
                onChange={handleChange}
                error={errors.surname}
                placeholder={t('projects.microsite.interest_form.surname_placeholder')}
              />
              <FormField
                label={t('projects.microsite.interest_form.email_label')}
                name="email"
                type="email"
                required
                value={form.email}
                onChange={handleChange}
                error={errors.email}
                placeholder={t('projects.microsite.interest_form.email_placeholder')}
              />
              <FormField
                label={t('projects.microsite.interest_form.phone_label')}
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                error={errors.phone}
                placeholder={t('projects.microsite.interest_form.phone_placeholder')}
              />
              <Turnstile
                ref={turnstileRef}
                siteKey={turnstileSiteKey}
                onSuccess={(token) => {
                  setTurnstileToken(token);
                  setTurnstileError('');
                }}
                onExpire={() => setTurnstileToken('')}
                onError={(errorCode) => {
                  setTurnstileToken('');
                  // Codes starting with 3 or 6 mean Turnstile ran and rejected the visitor.
                  // Other families (1xxxxx/2xxxxx/4xxxxx) mean Turnstile itself failed to
                  const isVerificationFailure = /^[36]/.test(String(errorCode));
                  if (isVerificationFailure) {
                    setTurnstileError('projects.microsite.interest_form.error_turnstile_verify');
                  } else {

                    setTurnstileError('projects.microsite.interest_form.error_turnstile_load');
                    console.warn(`Turnstile failed to load or initialize (code ${errorCode})`);
                  }
                }}
                scriptOptions={{
                  onError: () => {
                    console.warn('Turnstile script failed to load');
                    setTurnstileError('projects.microsite.interest_form.error_turnstile_fail');
                  }
                }}
              />
            </div>
          </div>

          {/* Footer — submit button */}
          <div className="px-4 pb-6 flex flex-col items-stretch md:items-end">
            {errors.submit && (
              <p className="mb-2 text-sm text-red-600 text-center md:text-right">
                {errors.submit}
              </p>
            )}
            {turnstileError && (
              <p className="mb-2 text-sm text-red-600 text-center px-2 md:text-left w-1/2">
                {t(turnstileError)}
              </p>
            )}
            <PrimaryButton type="submit" loading={isLoading} loadingLabel={t('projects.microsite.interest_form.submitting_label')}>
              {t('projects.microsite.interest_form.submit_button')}
            </PrimaryButton>
          </div>
        </form>
      )}
    </article>
  );
}
